import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { Navbar } from "./shared/Navbar";
import darkTheme from "./theme/darkTheme";
import lightTheme from "./theme/lightTheme";
import { SystemDataInitializer } from "./SystemDataInitializer";
import { useKeepAlive } from "./hooks/useKeepAlive";
import { AppRoutes } from "./routes";
import { AuthInitializer } from "./shared/AuthInitializer";

/**
 * AgentApp — rendered for all authenticated agent routes.
 * Owns: Navbar, theme, SystemDataInitializer, useKeepAlive, print handling.
 */
const AgentApp: React.FC = () => {
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);

  const isPrintPage = location.pathname.includes("/print");
  const isMeetingModePage = location.pathname.includes("/meeting-mode");
  const isShellLessPage = isPrintPage || isMeetingModePage;
  // /admin/companies mounts without SystemDataInitializer (heavy fund data not needed there)
  const skipSystemInit = location.pathname.startsWith("/admin/companies");

  useKeepAlive(60 * 60 * 1000);

  const theme = isDarkMode ? darkTheme : lightTheme;

  const routes = skipSystemInit ? (
    <AppRoutes />
  ) : (
    <SystemDataInitializer>
      <AppRoutes />
    </SystemDataInitializer>
  );

  return (
    <MuiThemeProvider theme={theme}>
      <StyledThemeProvider theme={theme}>
        <CssBaseline />
        <AuthInitializer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: isPrintPage ? "auto" : "100vh",
              width: "100%",
              overflow: isPrintPage ? "visible" : "hidden",
            }}
          >
            {!isShellLessPage && (
              <Navbar toggleTheme={() => setIsDarkMode((prev) => !prev)} />
            )}

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                width: "100%",
                maxWidth: "100%",
                overflowY: isPrintPage ? "visible" : "auto",
                overflowX: "hidden",
              }}
            >
              {routes}
            </div>
          </div>
        </AuthInitializer>

        <ReactQueryDevtools initialIsOpen={false} />
      </StyledThemeProvider>
    </MuiThemeProvider>
  );
};

export default AgentApp;
